import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  MdArrowBack,
  MdChevronRight,
  MdClose,
  MdLightbulbOutline,
  MdPalette,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import { useTranslation } from "react-i18next";
import type { StyleSpecification } from "maplibre-gl";
import {
  getSlotColor,
  isComponentVisible,
  scaleLabelSize,
  setComponentVisible,
  setSlotColor,
} from "../../libs/simple/apply";
import { normalizeColor } from "../../libs/simple/colors";
import { COMPONENT_TITLES, sectionTitle } from "../../libs/simple/labels";
import type { MainColor } from "../../libs/simple/main";
import { spotlightLayers } from "../../libs/simple/spotlight";
import type {
  ColorPath,
  ComponentId,
  SimpleComponent,
  Slot,
} from "../../libs/simple/types";
import { dismissHint, isHintDismissed } from "../../libs/preferences";
import InputButton from "../InputButton";
import AiPanel from "./AiPanel";
import ComponentIcon from "./ComponentIcon";
import GlobalAdjust from "./GlobalAdjust";
import MainSwatch from "./MainSwatch";
import SimpleSwatch from "./SimpleSwatch";
import StyleGallery from "./StyleGallery";
import { useMainColors } from "./useMainColors";
import { PreviewSession, usePreviewSession } from "./usePreviewSession";
import { useSimpleModel } from "./useSimpleModel";

export type SimpleSelection = ComponentId | "all" | null;

type MapStyle = StyleSpecification & { id: string };

type SimplePanelProps = {
  mapStyle: MapStyle;
  selection: SimpleSelection;
  onSelect(selection: SimpleSelection): void;
  // Layers to keep bright while the user points at a colour, or null.
  onSpotlight?(layerIds: string[] | null): void;
  onPreview(mapStyle: MapStyle): void;
  onCommit(mapStyle: MapStyle): void;
  // Hands the app the panel's preview session while the panel is mounted,
  // so Save, Export and Undo can settle a change still in progress.
  onSession?(session: PreviewSession | null): void;
  // The map's current zoom, for colours that change with zoom.
  zoom?: number;
  aiOpen: boolean;
  onAiToggle(): void;
  onOpenGallery(): void;
  // Opens a preset style directly, from the start screen.
  onOpenStyle?(mapStyle: MapStyle): void;
  onSwitchToAdvanced(): void;
};

const LABEL_COMPONENTS: ComponentId[] = ["labels", "pois"];
const SPOTLIGHT_DELAY = 250;
const SIZE_STEP = 1.1;

function groupBySection(slots: Slot[]): { title: string; slots: Slot[] }[] {
  const groups: { title: string; slots: Slot[] }[] = [];
  slots.forEach((slot) => {
    const title = sectionTitle(slot.section, slot.role);
    let group = groups.find((candidate) => candidate.title === title);
    if (!group) {
      group = { title, slots: [] };
      groups.push(group);
    }
    group.slots.push(slot);
  });
  return groups;
}

type MainListProps = {
  mains: MainColor[];
  mapStyle: MapStyle;
  session: PreviewSession;
  zoomControls?: boolean;
  zoom?: number;
  zoomOpened?: Set<string>;
  onZoomOpened?(id: string, opened: boolean): void;
  onPoint?(paths: ColorPath[] | null): void;
};

// The named colours a customer thinks in: Water, Motorways, Place names.
function MainList(props: MainListProps) {
  return (
    <>
      {props.mains.map((main) => (
        <MainSwatch
          key={main.id}
          main={main}
          mapStyle={props.mapStyle}
          session={props.session}
          zoomControls={props.zoomControls}
          zoom={props.zoom}
          zoomOpened={props.zoomOpened?.has(main.id)}
          onZoomOpened={(opened) => props.onZoomOpened?.(main.id, opened)}
          onPoint={(pointing) => props.onPoint?.(pointing ? main.paths : null)}
        />
      ))}
    </>
  );
}

type SlotListProps = {
  slots: Slot[];
  mapStyle: MapStyle;
  session: PreviewSession;
  onPoint?(paths: ColorPath[] | null): void;
};

// Every individual shade, for fine-tuning.
function SlotList(props: SlotListProps) {
  const { t } = useTranslation();
  const { session } = props;
  return (
    <>
      {groupBySection(props.slots).map((group) => (
        <section className="studio-simple__section" key={group.title}>
          <h3 className="studio-simple__section-title">{t(group.title)}</h3>
          {group.slots.map((slot) => {
            const color = getSlotColor(props.mapStyle, slot);
            if (!color) return null;
            return (
              <SimpleSwatch
                key={slot.key}
                slotKey={slot.key}
                label={slot.label}
                // Shown the way main colours are; the style keeps its own
                // spelling until the user changes the colour.
                color={normalizeColor(color)}
                onPreview={(next) =>
                  session.preview((base) => setSlotColor(base, slot, next))
                }
                onCommitPreview={session.commit}
                onCancelPreview={session.cancel}
                onApply={(next) =>
                  session.apply((current) => setSlotColor(current, slot, next))
                }
                onPoint={(pointing) => props.onPoint?.(pointing ? slot.paths : null)}
              />
            );
          })}
        </section>
      ))}
    </>
  );
}

export default function SimplePanel(props: SimplePanelProps) {
  const { t } = useTranslation();
  const { mapStyle, selection, onSelect } = props;
  const session = usePreviewSession(mapStyle, props.onPreview, props.onCommit);
  const model = useSimpleModel(mapStyle, session);
  const mains = useMainColors(mapStyle, session);

  const mainsOf = (id: ComponentId) =>
    mains.filter((main) => main.component === id);

  const selected: SimpleComponent | undefined = model.components.find(
    (component) => component.id === selection
  );
  const showAll = selection === "all" && model.slots.length > 0;

  // A selection that no longer exists (another style was opened) must not
  // come back to life when a later style has that component again.
  const stale = selection !== null && !selected && !showAll;
  useEffect(() => {
    if (stale) onSelect(null);
  }, [stale, onSelect]);

  // The overview, a component's detail and the AI view share one scroll
  // area. A detail or the AI view starts at its top, or it would begin below
  // its own title. Coming back to the overview returns to where the user
  // left it, so the next component is still under the cursor.
  const view = props.aiOpen ? "ai" : selection === null ? "overview" : selection;
  const root = useRef<HTMLDivElement>(null);
  const shownView = useRef(view);
  const overviewScroll = useRef(0);

  useEffect(() => {
    const scroller = root.current?.closest(".studio-scroll-container");
    if (!scroller) return;
    // Scroll events that arrive after the overview was left (the browser
    // clamping the position to a shorter screen) must not overwrite it.
    const remember = () => {
      if (shownView.current === "overview") {
        overviewScroll.current = scroller.scrollTop;
      }
    };
    scroller.addEventListener("scroll", remember, { passive: true });
    return () => scroller.removeEventListener("scroll", remember);
  }, []);

  useLayoutEffect(() => {
    const previous = shownView.current;
    if (previous === view) return;
    shownView.current = view;
    root.current
      ?.closest(".studio-scroll-container")
      ?.scrollTo(0, view === "overview" ? overviewScroll.current : 0);

    // Keyboard and screen-reader users follow the change of view: into a
    // detail or the AI view the focus goes to its back button, and back in
    // the overview to whatever opened the view that was left. Without
    // scrolling, which was settled above.
    const find = (name: string, scope: ParentNode | null = root.current) =>
      scope?.querySelector<HTMLElement>(`[data-wd-key="${name}"]`);
    let target: HTMLElement | null | undefined;
    if (view !== "overview") {
      target = root.current?.querySelector<HTMLElement>(".studio-simple__back");
    } else if (previous === "ai") {
      target = find("nav:ai", document);
    } else if (previous === "all") {
      target = find("simple:all");
    } else {
      target = find(`simple:component:${previous}`);
    }
    target?.focus({ preventScroll: true });
  }, [view]);

  const latest = useRef(props);
  latest.current = props;

  // Pointing at a colour for a moment lights up where it is used. The delay
  // keeps the map still while the pointer merely crosses the panel.
  const spotlightTimer = useRef<number>();
  const spotlit = useRef(false);
  const point = (paths: ColorPath[] | null) => {
    window.clearTimeout(spotlightTimer.current);
    if (!paths) {
      if (spotlit.current) latest.current.onSpotlight?.(null);
      spotlit.current = false;
      return;
    }
    const layerIds = [...spotlightLayers(paths)];
    spotlightTimer.current = window.setTimeout(() => {
      spotlit.current = true;
      latest.current.onSpotlight?.(layerIds);
    }, SPOTLIGHT_DELAY);
  };

  // Leaving the panel or changing view never leaves the map faded.
  useEffect(() => {
    return () => {
      window.clearTimeout(spotlightTimer.current);
      if (spotlit.current) latest.current.onSpotlight?.(null);
    };
  }, []);
  useEffect(() => point(null), [view]);

  useEffect(() => {
    latest.current.onSession?.(session);
    return () => latest.current.onSession?.(null);
  }, [session]);

  // Leaving Simple mode closes the AI view, or it would be the first thing
  // shown on the way back. Its own unmount has already cancelled a preview.
  useEffect(() => {
    return () => {
      if (latest.current.aiOpen) latest.current.onAiToggle();
    };
  }, []);

  const [hintShown, setHintShown] = useState(() => !isHintDismissed());
  // Families whose zoom colours the user switched on during this visit.
  const [zoomOpened, setZoomOpened] = useState<Set<string>>(() => new Set());
  const onZoomOpened = (id: string, opened: boolean) =>
    setZoomOpened((before) => {
      const next = new Set(before);
      if (opened) next.add(id);
      else next.delete(id);
      return next;
    });

  const header = (title: string, icon?: ComponentId) => (
    <header className="studio-simple__header">
      <button
        type="button"
        className="studio-simple__back"
        aria-label={t("Back to overview")}
        data-wd-key="simple:back"
        onClick={() => onSelect(null)}
      >
        <MdArrowBack />
      </button>
      {icon && <ComponentIcon id={icon} />}
      <h2 className="studio-simple__title">{title}</h2>
    </header>
  );

  let body;
  if (props.aiOpen) {
    body = <AiPanel onClose={props.onAiToggle} mains={mains} session={session} />;
  } else if (model.components.length === 0) {
    body = (
      <div className="studio-simple__empty" data-wd-key="simple:empty">
        <h2 className="studio-simple__title">{t("Start from a style")}</h2>
        <p className="studio-simple__lead">
          {t("This style has no layers yet. Pick one to start from, then make it yours.")}
        </p>
        {props.onOpenStyle ? (
          <StyleGallery onOpenStyle={props.onOpenStyle} />
        ) : null}
        <InputButton
          className="studio-button--tinted"
          onClick={props.onOpenGallery}
          data-wd-key="simple:gallery"
        >
          {t("More styles and your saved ones")}
        </InputButton>
        <InputButton
          onClick={props.onSwitchToAdvanced}
          data-wd-key="simple:to-advanced"
        >
          {t("Switch to Advanced")}
        </InputButton>
      </div>
    );
  } else if (showAll) {
    body = (
      <>
        {header(t("All colours"))}
        {model.components.map((component) => (
          <div key={component.id}>
            <h2 className="studio-simple__group-title">
              <ComponentIcon id={component.id} />
              {t(COMPONENT_TITLES[component.id])}
            </h2>
            <SlotList
              slots={component.slots}
              mapStyle={mapStyle}
              session={session}
              onPoint={point}
            />
          </div>
        ))}
      </>
    );
  } else if (selected) {
    const selectedMains = mainsOf(selected.id);
    body = (
      <>
        {header(t(COMPONENT_TITLES[selected.id]), selected.id)}
        {selectedMains.length > 0 && (
          <section className="studio-simple__section">
            <h3 className="studio-simple__subtitle">{t("Main colours")}</h3>
            <MainList
              mains={selectedMains}
              mapStyle={mapStyle}
              session={session}
              zoomControls
              zoom={props.zoom}
              zoomOpened={zoomOpened}
              onZoomOpened={onZoomOpened}
              onPoint={point}
            />
          </section>
        )}
        {LABEL_COMPONENTS.includes(selected.id) && (
          <div className="studio-simple__size">
            <span>{t("Label size")}</span>
            <InputButton
              aria-label={t("Smaller labels")}
              data-wd-key="simple:size:down"
              onClick={() =>
                session.apply((current) =>
                  scaleLabelSize(current, selected, 1 / SIZE_STEP)
                )
              }
            >
              A−
            </InputButton>
            <InputButton
              aria-label={t("Larger labels")}
              data-wd-key="simple:size:up"
              onClick={() =>
                session.apply((current) =>
                  scaleLabelSize(current, selected, SIZE_STEP)
                )
              }
            >
              A+
            </InputButton>
          </div>
        )}
        {selected.slots.length === 0 ? (
          <p className="studio-simple__note">
            {t("Nothing to recolour here. Use Advanced for these layers.")}
          </p>
        ) : (
          <h3 className="studio-simple__subtitle" data-wd-key="simple:finetune">
            {t("Fine-tune")}
          </h3>
        )}
        <SlotList
          slots={selected.slots}
          mapStyle={mapStyle}
          session={session}
          onPoint={point}
        />
      </>
    );
  } else {
    const onlyOther = model.components.every(
      (component) => component.id === "other"
    );
    body = (
      <>
        {onlyOther && (
          <p className="studio-simple__note" data-wd-key="simple:unrecognised">
            {t(
              "This style uses layers Simple mode does not recognise. Advanced gives full control."
            )}{" "}
            <button
              type="button"
              className="studio-simple__link"
              onClick={props.onSwitchToAdvanced}
            >
              {t("Switch to Advanced")}
            </button>
          </p>
        )}
        {hintShown && (
          <div className="studio-hint" role="note" data-wd-key="simple:hint">
            <MdLightbulbOutline className="studio-hint__icon" aria-hidden="true" />
            <p className="studio-hint__text">
              {t("Pick a colour below, or click the map to find what to change.")}
            </p>
            <button
              type="button"
              className="studio-hint__close"
              aria-label={t("Dismiss hint")}
              title={t("Dismiss hint")}
              data-wd-key="simple:hint:close"
              onClick={() => {
                dismissHint();
                setHintShown(false);
              }}
            >
              <MdClose />
            </button>
          </div>
        )}
        <GlobalAdjust session={session} />
        <ul className="studio-simple__list">
          {model.components.map((component) => {
            const visible = isComponentVisible(mapStyle, component);
            const title = t(COMPONENT_TITLES[component.id]);
            const toggleLabel = visible
              ? t("Hide {{name}}", { name: title })
              : t("Show {{name}}", { name: title });
            return (
              <li className="studio-simple__item" key={component.id}>
                <div className="studio-simple__row">
                  <button
                    type="button"
                    className="studio-simple__open"
                    data-wd-key={`simple:component:${component.id}`}
                    onClick={() => onSelect(component.id)}
                  >
                    <ComponentIcon id={component.id} />
                    <span className="studio-simple__name">{title}</span>
                    <span className="studio-simple__more">{t("Fine-tune")}</span>
                    <MdChevronRight />
                  </button>
                  <button
                    type="button"
                    className="studio-simple__eye"
                    aria-label={toggleLabel}
                    title={toggleLabel}
                    data-wd-key={`simple:component:${component.id}:toggle`}
                    onClick={() =>
                      session.apply((current) =>
                        setComponentVisible(current, component, !visible)
                      )
                    }
                  >
                    {visible ? <MdVisibility /> : <MdVisibilityOff />}
                  </button>
                </div>
                <div className="studio-simple__mains">
                  <MainList
                    mains={mainsOf(component.id)}
                    mapStyle={mapStyle}
                    session={session}
                    onPoint={point}
                  />
                </div>
              </li>
            );
          })}
        </ul>
        <div className="studio-simple__footer">
          <InputButton onClick={() => onSelect("all")} data-wd-key="simple:all">
            <MdPalette /> {t("All colours")}
          </InputButton>
          <InputButton onClick={props.onOpenGallery} data-wd-key="simple:gallery">
            {t("Start from a style")}
          </InputButton>
        </div>
      </>
    );
  }

  return (
    <div className="studio-simple" data-wd-key="simple:panel" ref={root}>
      {body}
    </div>
  );
}
